import { CreateUserDto, UpdateUserDto, User, PasswordResetDto, ChangePasswordDto } from '../types/user';
import axios from 'axios';
import { API_BASE_URL } from '../config';

// Note: Mock users removed - now using real APIs

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor für JWT Token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    if (!config.headers) {
      config.headers = {};
    }
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response Interceptor für Error Handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper function to normalize user data between backend (snake_case) and frontend (camelCase)
const normalizeUserData = (user: any): User => {
  return {
    ...user,
    // Ensure both formats are available for backward compatibility
    employeeNumber: user.employee_number || user.employeeNumber,
    employee_number: user.employee_number || user.employeeNumber,
    firstName: user.first_name || user.firstName,
    first_name: user.first_name || user.firstName,
    lastName: user.last_name || user.lastName,
    last_name: user.last_name || user.lastName,
    isActive: user.is_active !== undefined ? user.is_active : user.isActive,
    is_active: user.is_active !== undefined ? user.is_active : user.isActive,
    createdAt: user.created_at || user.createdAt,
    created_at: user.created_at || user.createdAt,
    updatedAt: user.updated_at || user.updatedAt,
    updated_at: user.updated_at || user.updatedAt,
  };
};

export const userService = {
  // Benutzer abrufen
  getUsers: async (): Promise<User[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<User[]>(
      '/users',
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data.map(normalizeUserData);
  },

  // Einzelnen Benutzer abrufen
  getUser: async (id: number): Promise<User> => {
    const response = await api.get<User>(`/users/${id}`);
    return normalizeUserData(response.data);
  },

  // Neuen Benutzer erstellen
  createUser: async (userData: CreateUserDto): Promise<User> => {
    const response = await api.post<User>('/users', userData);
    return normalizeUserData(response.data);
  },

  // Benutzer aktualisieren
  updateUser: async (id: number, userData: UpdateUserDto): Promise<User> => {
    const response = await api.put<User>(`/users/${id}`, userData);
    return normalizeUserData(response.data);
  },

  // Benutzer aktivieren/deaktivieren
  toggleUserStatus: async (id: number, isActive: boolean): Promise<User> => {
    const response = await api.patch<User>(`/users/${id}/status`, { is_active: isActive });
    return normalizeUserData(response.data);
  },

  // Benutzer löschen
  deleteUser: async (id: number): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  // Passwort zurücksetzen (direkte Vergabe durch Admin)
  adminResetPassword: async (userId: number, newPassword: string): Promise<void> => {
    await api.post(`/auth/password-reset/${userId}`, {
      new_password: newPassword
    });
  },

  // Passwort ändern
  changePassword: async (id: number, data: ChangePasswordDto): Promise<void> => {
    await api.post(`/users/${id}/change-password`, {
      current_password: data.oldPassword,
      new_password: data.newPassword
    });
  },
}; 